import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  CreditCard,
  FileSpreadsheet,
  FileText,
  Users,
  Landmark,
  ClipboardList,
  Package,
  ShoppingCart,
  ShoppingBag,
  Store,
  PlusCircle,
  Truck,
  Star,
  LogOut,
  ChevronsUpDown,
} from 'lucide-react'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

const memberNav = [
  {
    label: 'Overview',
    items: [
      { title: 'Dashboard', url: '/customer/dashboard', icon: LayoutDashboard },
      { title: 'My Loans', url: '/customer/loans', icon: CreditCard },
      { title: 'Financial Data', url: '/customer/financial-data', icon: FileSpreadsheet },
      { title: 'My Statements', url: '/customer/my-statements', icon: FileText },
    ],
  },
  {
    label: 'MG Katale',
    items: [
      { title: 'Marketplace', url: '/katale', icon: Store },
      { title: 'Browse Products', url: '/katale/products', icon: ShoppingBag },
      { title: 'My Cart', url: '/katale/cart', icon: ShoppingCart },
      { title: 'My Products', url: '/katale/my-products', icon: Package },
      { title: 'Add Product', url: '/katale/add-product', icon: PlusCircle },
      { title: 'Orders', url: '/katale/orders', icon: Truck },
      { title: 'Ratings', url: '/katale/ratings', icon: Star },
    ],
  },
]

const adminNav = [
  {
    label: 'Management',
    items: [
      { title: 'Dashboard', url: '/admin/dashboard', icon: LayoutDashboard },
      { title: 'Customers', url: '/admin/customers', icon: Users },
      { title: 'Financial Statements', url: '/admin/financial-statements', icon: FileText },
      { title: 'Settlements', url: '/admin/settlements', icon: Landmark },
      { title: 'Data Entry', url: '/admin/financial-data-entry', icon: ClipboardList },
      { title: 'Product Profiles', url: '/admin/product-profiles', icon: Package },
    ],
  },
]

const AppSidebar = ({ role }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { setOpenMobile } = useSidebar()
  const { customer, logout: customerLogout } = useCustomerAuth()
  const { admin, logout: adminLogout } = useAdminAuth()

  const isAdmin = role === 'admin'
  const navGroups = isAdmin ? adminNav : memberNav
  const username = isAdmin
    ? (admin?.fullName || admin?.full_name || admin?.username)
    : (customer?.fullName || customer?.client_name || customer?.name || customer?.username || customer?.memberNo)
  const roleLabel = isAdmin
    ? 'MG Admin'
    : (customer?.memberNo ? `Member · ${customer.memberNo}` : 'Member')
  const homeUrl = isAdmin ? '/admin/dashboard' : '/customer/dashboard'

  const handleLogout = () => {
    if (isAdmin) {
      adminLogout()
      navigate('/admin/login')
    } else {
      customerLogout()
      navigate('/customer/login')
    }
  }

  const isItemActive = (url) => {
    if (url === '/katale') {
      return location.pathname === '/katale'
    }
    return location.pathname === url || location.pathname.startsWith(`${url}/`)
  }

  const initials = (username || '?')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip="UFCC Enterprise">
              <Link to={homeUrl} onClick={() => setOpenMobile(false)}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-white ring-1 ring-sidebar-border">
                  <img
                    src="/images/UFCC_Logo.png"
                    alt="UFCC Logo"
                    className="size-6 object-contain"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">UFCC Enterprise</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">{roleLabel}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={isItemActive(item.url)}
                      tooltip={item.title}
                    >
                      <Link to={item.url} onClick={() => setOpenMobile(false)}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{username}</span>
                    <span className="truncate text-xs text-sidebar-foreground/70">{roleLabel}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="top"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="grid text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{username}</span>
                    <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

export default AppSidebar
